import { useCallback, useMemo, useState } from "react";
import { CONTRACT_STATUS_OVERRIDES, ContractStatus, IContractStatusEntry, getDefaultContractStatus } from "./contractStatus";

const STORAGE_KEY = "@conducco:operationalPanel:contractStatusOverrides";

function readSessionOverrides(): IContractStatusEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as IContractStatusEntry[]) : [];
  } catch {
    return [];
  }
}

function writeSessionOverrides(entries: IContractStatusEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

export function useContractStatus() {
  const [sessionOverrides, setSessionOverrides] = useState<IContractStatusEntry[]>(readSessionOverrides);

  // Prioridade: sessão atual > arquivo versionado > regra de partida por seller/CNPJ.
  const overridesByWorkspaceId = useMemo(() => {
    const map = new Map<string, IContractStatusEntry>();
    [...CONTRACT_STATUS_OVERRIDES, ...sessionOverrides].forEach((entry) => map.set(entry.workspaceId, entry));
    return map;
  }, [sessionOverrides]);

  const getContractStatus = useCallback(
    (workspaceId: string, sellerName: string, buyerFederalTaxId: string): ContractStatus =>
      overridesByWorkspaceId.get(workspaceId)?.status ?? getDefaultContractStatus(sellerName, buyerFederalTaxId),
    [overridesByWorkspaceId]
  );

  const setContractStatus = useCallback((workspaceId: string, status: ContractStatus, author: string) => {
    const entry: IContractStatusEntry = { workspaceId, status, author, updatedAt: new Date().toISOString() };

    setSessionOverrides((current) => {
      const next = [...current.filter((item) => item.workspaceId !== workspaceId), entry];
      writeSessionOverrides(next);
      return next;
    });
  }, []);

  /** Mesmo padrão de "copiar marcações como código" do Silenciar — trecho pronto pra colar em contractStatus.ts. */
  const generateCodeSnippet = useCallback(() => {
    return sessionOverrides
      .map(
        (entry) =>
          `  { workspaceId: "${entry.workspaceId}", status: "${entry.status}", author: "${entry.author}", updatedAt: "${entry.updatedAt}" },`
      )
      .join("\n");
  }, [sessionOverrides]);

  return {
    sessionOverrides,
    getContractStatus,
    setContractStatus,
    generateCodeSnippet,
  };
}
